import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { downloadEcard } from '@/services/account'
import { ApiError } from '@/services/http'

/**
 * Download the member's third-party-payer e-card (PNG/PDF) as a file.
 * Shared by the card page and the dashboard. SSR-safe (no-op without `window`).
 *
 * On failure the backend returns `{ errors: '<code>' }` (422). Known Almerys
 * codes (card not found / unavailable, …) get a dedicated localized message;
 * anything else falls back to a generic download error.
 */
const KNOWN_ECARD_ERRORS = new Set([
  'almerys_ecard_not_found',
  'almerys_ecards_unavailable',
  'inter_platforms_reference_required',
])

/** Extract the backend error code from a failed download, or 'generic'. */
function ecardErrorCode(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body !== null) {
    const code = (err.body as { errors?: unknown }).errors
    if (typeof code === 'string') return code
  }
  return 'generic'
}

export function useEcard() {
  const { t } = useI18n()
  const downloading = ref(false)
  const errorCode = ref<string | null>(null)

  /** Localized message for the current error code (empty when no error). */
  const errorMessage = computed(() => {
    const code = errorCode.value
    if (!code) return ''
    return KNOWN_ECARD_ERRORS.has(code)
      ? t(`auth.account.tpCard.errors.${code}`)
      : t('auth.account.tpCard.downloadError')
  })

  async function download(format: 'png' | 'pdf' = 'pdf'): Promise<void> {
    if (downloading.value || typeof window === 'undefined') return
    downloading.value = true
    errorCode.value = null
    try {
      const blob = await downloadEcard(format)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `carte-tiers-payant.${format}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      errorCode.value = ecardErrorCode(err)
    } finally {
      downloading.value = false
    }
  }

  return { downloading, errorCode, errorMessage, download }
}
